import React, { useState, useEffect } from "react";
import { Box, Typography, Avatar, Chip, Card, CardContent, Grid, Divider } from "@mui/material";
import { useSelector } from "react-redux";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { format, isValid, parse } from "date-fns";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";

const DailyLogsHistory = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const user = useSelector((state) => state.auth.user);
  const [selectedTask, setSelectedTask] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [supports, setSupports] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [childs, setChilds] = useState([]);
  const [rooms, setRooms] = useState([]);

  const fetchData = async (endpoint, setter) => {
    try {
      const response = await axios.get(endpoint);

      const filtered = response.data.filter((task) => {
        return task.status === "completed";
      });
      setter(filtered);
    } catch (error) {
      console.error(`Error fetching data from ${endpoint}:`, error);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await axios.get("/api/assessments/assetAll", {
        headers: { "x-user-role": user?.roleid },
      });
      const completedTasks = response.data.filter((task) => {
        return task.status === "completed";
      });
      setTasks(completedTasks);
    } catch (error) {
      console.error("Error fetching tasks:", error);
    }
  };

  const fetchLogs = async () => {
    try {
      const response = await axios.get("/api/dailyLog/logAll");
      setLogs(response.data);
    } catch (error) {
      console.error("Error fetching daily logs:", error);
    }
  };

  useEffect(() => {
    fetchTasks();
    fetchData("/api/support/supportStatementAll", setSupports);
    fetchData("/api/staff/staffDebriefAll", setStaffs);
    fetchData("/api/child/childDebriefAll", setChilds);
    fetchData("/api/room/roomSearchAll", setRooms);
    fetchLogs();
  }, []);

  const allLogs = [...logs, ...tasks, ...supports, ...staffs, ...childs, ...rooms];

  const getCategoryLabel = (log) => {
    if (log.category) return log.category;
    const name = log.taskName || "";
    if (name.includes("Support")) return "Support Statement";
    if (name.includes("Staff")) return "Staff Debrief";
    if (name.includes("Child")) return "Child Debrief";
    if (name.includes("Room")) return "Room Search";
    return "Tasks";
  };

  const getFormattedTime = (log) => {
    if (log.time && isValid(parse(log.time, "HH:mm", new Date()))) {
      return format(parse(log.time, "HH:mm", new Date()), "hh:mm a");
    }
    return isValid(new Date(log.updated_at)) ? format(new Date(log.updated_at), "hh:mm a") : "Invalid time";
  };

  return (
    <Box sx={{ pl: 4, pr: 4, ml: 2 }}>
      <Typography sx={{ fontSize: "24px", fontWeight: "bold", color: "black" }}>Daily Logs | History</Typography>
      <Grid container spacing={2} mt={2}>
        <Grid item lg={9} md={9}>
          <Typography sx={{ fontSize: "16px", color: "black" }}>
            <Grid container>
              <Grid item>
                <KeyboardBackspaceIcon onClick={() => navigate("/daily-logs")} style={{ cursor: "pointer" }} />
              </Grid>
              <Grid item> Back to Daily Log </Grid>
            </Grid>
          </Typography>
          <Typography variant='body1' color='gray'>
            Records all daily activities, incidents, etc.
          </Typography>

          {/* Table Header */}
          <Box sx={{ mt: 2, mb: 2 }} className='scrollable-task-list'>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                backgroundColor: "#E3F2FD",
                padding: "8px 16px",
                borderRadius: "8px",
              }}
            >
              <Grid container alignItems='center' spacing={2}>
                {["Time", "Date", "Description", "Category", "Reported by", "Status"].map((label, i) => (
                  <Grid item xs={2} key={i}>
                    <Typography sx={{ fontSize: "14px", color: "blue" }}>{label}</Typography>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Table Content */}
            {allLogs.length > 0 ? (
              allLogs.map((log, index) => (
                <Card key={index} sx={{ mt: 1, boxShadow: 1 }} onClick={() => setSelectedTask(log)}>
                  <CardContent>
                    <Grid container alignItems='center' spacing={2}>
                      <Grid item xs={2}>
                        <Typography sx={{ fontSize: "14px" }}>{getFormattedTime(log)}</Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Typography sx={{ fontSize: "14px" }}>
                          {new Date(log.updated_at).toISOString().split("T")[0]}
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Typography sx={{ fontSize: "14px" }}>{log.description}</Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Chip
                          label={getCategoryLabel(log)}
                          sx={{ backgroundColor: log.categoryColor, color: "#333" }}
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <Box display='flex' alignItems='center'>
                          <Avatar src='/images/person.png' sx={{ width: 40, height: 40, mr: 2 }} />
                          {log.reportedBy || log.updated_by}
                        </Box>
                      </Grid>
                      <Grid item xs={2}>
                        <Chip
                          label={log.status}
                          sx={{
                            backgroundColor: log.status === "completed" ? "#EAFFF2" : "#FFDDCC",
                            color: log.status === "completed" ? "green" : "red",
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Box display='flex' justifyContent='center'>
                <img src='/images/file.gif' alt='Loading...' width='200' height='200' />
              </Box>
            )}
          </Box>
        </Grid>

        <Grid item lg={3} md={3}>
          {selectedTask ? (
            <Box sx={{ p: 2, ml: 2 }}>
              <Typography sx={{ fontSize: "20px", color: "black", fontWeight: "bold", mb: 2 }}>More Info</Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography sx={{ fontSize: "14px", color: "black", fontWeight: "bold", mb: 2 }}>
                {selectedTask.category || "Tasks"}
              </Typography>

              <Typography sx={{ fontSize: "14px", mb: 3 }}>{selectedTask.description}</Typography>

              <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
                <Grid item lg={6} md={6}>
                  <Typography sx={{ fontSize: "14px" }}>Category</Typography>
                </Grid>
                <Grid item lg={6} md={6}>
                  <Typography sx={{ fontSize: "14px" }}>{selectedTask.category || "Tasks"}</Typography>
                </Grid>
              </Grid>

              <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
                <Grid item lg={6} md={6}>
                  <Typography sx={{ fontSize: "14px" }}>Status</Typography>
                </Grid>
                <Grid item lg={6} md={6}>
                  <Chip
                    variant='contained'
                    sx={{
                      fontSize: "12px",
                      borderRadius: 5,
                      backgroundColor: selectedTask.status === "completed" ? "#EAFFF2" : "#FFDDCC",
                      color: selectedTask.status === "completed" ? "green" : "#FF5733",
                    }}
                    label={selectedTask.status}
                    size='small'
                  />
                </Grid>
              </Grid>

              <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
                <Grid item lg={6} md={6}>
                  <Typography sx={{ fontSize: "14px" }}>Assigned by</Typography>
                </Grid>
                <Grid item lg={6} md={6}>
                  <Typography sx={{ fontSize: "14px" }}>
                    {selectedTask.reportedBy || selectedTask.updated_by}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
                <Grid item lg={6} md={6}>
                  <Typography sx={{ fontSize: "14px" }}>Time</Typography>
                </Grid>
                <Grid item lg={6} md={6}>
                  <Typography sx={{ fontSize: "14px" }}>
                    {selectedTask.time || format(new Date(selectedTask.updated_at), "hh.mm a ")}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Box sx={{ p: 2, ml: 2 }}>
              <Typography sx={{ fontSize: "20px", color: "black", fontWeight: "bold", mb: 2 }}>More Info</Typography>
              <Divider sx={{ mb: 2 }} />

              <Typography sx={{ fontSize: "14px", mb: 3 }}>Add records to see more details</Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default DailyLogsHistory;
